import { copyToClipboard } from '@/lib';
import { ChevronDown, Copy, ExternalLink, Key, Speed } from '@nosinovacao/fe-icons';
import { Button, Checkbox, cn, QrCode, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type ReactNode, useState } from 'react';
import { type RegisterProps } from '../-model/props';

const KEY_PREFIX = 'Security.AuthenticatorAppCard.Setup.Register';

const howToSteps = [
  `${KEY_PREFIX}.HowTo.Step1`,
  `${KEY_PREFIX}.HowTo.Step2`,
  `${KEY_PREFIX}.HowTo.Step3`,
  `${KEY_PREFIX}.HowTo.Step4`,
  `${KEY_PREFIX}.HowTo.Step5`,
];

export function RegisterLight({
  confirmed,
  googleSelected,
  goToValidate,
  platform,
  secret,
  setConfirmed,
  uri,
}: Readonly<RegisterProps>) {
  if (platform === null) {
    return <DesktopFlow goToValidate={goToValidate} secret={secret} uri={uri} />;
  }

  if (googleSelected) {
    return <QuickFlow goToValidate={goToValidate} secret={secret} uri={uri} />;
  }

  return (
    <StandardFlow
      confirmed={confirmed}
      goToValidate={goToValidate}
      secret={secret}
      setConfirmed={setConfirmed}
      uri={uri}
    />
  );
}

// ? desktop cannot deep link into the app, so the QR code is the primary path
function DesktopFlow({
  goToValidate,
  secret,
  uri,
}: Readonly<{ goToValidate: () => void; secret: string; uri: string }>) {
  const { t } = useI18n();
  return (
    <>
      <div className="grid flex-1 auto-rows-max content-start gap-12 overflow-y-auto pt-6">
        <Heading title={t(`${KEY_PREFIX}.Desktop.Title`)} subtitle={t(`${KEY_PREFIX}.Subtitle`)} />
        <QrCodeBox uri={uri} className="shadow-[2px_2px_2px_0_rgba(0,0,0,0.05)]" />
        <div className="grid gap-4">
          <SecretField secret={secret} variant="plain" />
          <HowToDisclosure />
        </div>
      </div>
      <Footer label={t(`${KEY_PREFIX}.Desktop.Continue`)} onClick={goToValidate} />
    </>
  );
}

// ? mobile with Google Authenticator picked: the otpauth uri opens the app directly
function QuickFlow({
  goToValidate,
  secret,
  uri,
}: Readonly<{ goToValidate: () => void; secret: string; uri: string }>) {
  const { t } = useI18n();
  return (
    <>
      <div className="grid flex-1 auto-rows-max content-start gap-4 overflow-y-auto">
        <Heading title={t(`${KEY_PREFIX}.Title`)} />
        <SpokeCard
          icon={<Speed width={56} height={56} />}
          title={t(`${KEY_PREFIX}.Quick.CardTitle`)}
        >
          <p className="w-full text-nos-neutral-900 font-ui-9">
            {t(`${KEY_PREFIX}.Quick.Description`)}
          </p>
          <Button
            skin="light"
            asChild
            data-tid="open-authenticator-button"
            className="w-full px-4 tablet:w-fit"
          >
            <a href={uri}>
              {t(`${KEY_PREFIX}.Quick.OpenApp`)}
              <ExternalLink width={20} height={20} />
            </a>
          </Button>
        </SpokeCard>
        <Disclosure summary={t(`${KEY_PREFIX}.OtherMethods`)} testId="other-methods-disclosure">
          <p className="text-nos-neutral-800 font-text-p2">{t(`${KEY_PREFIX}.Quick.UseKey`)}</p>
          <SecretField secret={secret} variant="panel" />
          <HowToDisclosure />
          <Divider />
          <QrCodeBlock uri={uri} />
        </Disclosure>
      </div>
      <Footer label={t(`${KEY_PREFIX}.Continue`)} onClick={goToValidate} />
    </>
  );
}

// ? mobile without Google Authenticator: the key has to be pasted by hand
function StandardFlow({
  confirmed,
  goToValidate,
  secret,
  setConfirmed,
  uri,
}: Readonly<{
  confirmed: boolean;
  goToValidate: () => void;
  secret: string;
  setConfirmed: (confirmed: boolean) => void;
  uri: string;
}>) {
  const { t } = useI18n();
  return (
    <>
      <div className="grid flex-1 auto-rows-max content-start gap-8 overflow-y-auto">
        <Heading title={t(`${KEY_PREFIX}.Title`)} />
        <SpokeCard
          icon={<Key width={56} height={56} />}
          title={t(`${KEY_PREFIX}.Standard.CardTitle`)}
        >
          <SecretField secret={secret} variant="card" />
        </SpokeCard>
        <HowTo />
        <Disclosure summary={t(`${KEY_PREFIX}.OtherMethods`)} testId="other-methods-disclosure">
          <QrCodeBlock uri={uri} />
        </Disclosure>
      </div>
      <Checkbox
        id="already-associated"
        data-tid="already-associated-checkbox"
        skin="light"
        checked={confirmed}
        onChange={() => setConfirmed(!confirmed)}
        label={t(`${KEY_PREFIX}.Standard.Confirm`).toString()}
      />
      <Footer label={t(`${KEY_PREFIX}.Continue`)} disabled={!confirmed} onClick={goToValidate} />
    </>
  );
}

function Heading({ subtitle, title }: Readonly<{ subtitle?: ReactNode; title: ReactNode }>) {
  return (
    <div className="grid gap-2">
      <h1 data-tid="header-title" className="font-bold text-nos-neutral-1000 font-title-h2">
        {title}
      </h1>
      {subtitle && (
        <p data-tid="header-subtitle" className="text-nos-neutral-800 font-text-p2">
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ? the field only needs its own white surface when it sits on one of the grey bands
const secretFieldVariants = {
  card: 'bg-nos-neutral-0 px-2 py-3',
  panel: 'bg-nos-neutral-0 p-2',
  plain: '',
};

function SecretField({
  secret,
  variant,
}: Readonly<{ secret: string; variant: keyof typeof secretFieldVariants }>) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-lg',
        secretFieldVariants[variant],
      )}
    >
      <span className="grid gap-2">
        {variant !== 'card' && (
          <span className="text-nos-neutral-800 font-text-p2">
            {t(`${KEY_PREFIX}.SecretLabel`)}
          </span>
        )}
        <span className="break-all font-medium text-nos-neutral-1000 font-text-p2">{secret}</span>
      </span>
      <button
        type="button"
        data-tid="copy-secret-button"
        aria-label={
          copied ? t(`${KEY_PREFIX}.Copied`).toString() : t(`${KEY_PREFIX}.Copy`).toString()
        }
        onClick={() => {
          void copyToClipboard(secret).then(setCopied);
        }}
        className="shrink-0 text-nos-neutral-900"
      >
        <Copy width={24} height={24} />
      </button>
    </div>
  );
}

function HowToSteps() {
  const { t } = useI18n();
  return (
    <ol className="grid gap-4">
      {howToSteps.map((step, index) => (
        <li key={step} className="grid grid-cols-[auto,1fr] items-center gap-2">
          <span
            aria-hidden="true"
            className="grid size-6 place-items-center rounded-full bg-nos-blue font-medium text-nos-neutral-0 font-ui-9"
          >
            {index + 1}
          </span>
          <span className="text-nos-neutral-1000 font-text-p2">{t(step)}</span>
        </li>
      ))}
    </ol>
  );
}

// ? the standard flow always shows the steps, so there the summary is a plain heading
function HowTo() {
  const { t } = useI18n();
  return (
    <div className="grid gap-3">
      <span className="font-medium text-nos-neutral-800 font-text-p2">
        {t(`${KEY_PREFIX}.HowTo.Title`)}
      </span>
      <HowToSteps />
    </div>
  );
}

function HowToDisclosure() {
  const { t } = useI18n();
  return (
    <Disclosure summary={t(`${KEY_PREFIX}.HowTo.Title`)} testId="how-to-disclosure" tertiary>
      <HowToSteps />
    </Disclosure>
  );
}

function QrCodeBlock({ uri }: Readonly<{ uri: string }>) {
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <p className="text-nos-neutral-800 font-text-p2">{t(`${KEY_PREFIX}.OtherDevice`)}</p>
      <QrCodeBox uri={uri} />
    </div>
  );
}

function QrCodeBox({ className, uri }: Readonly<{ className?: string; uri: string }>) {
  return (
    <div className={cn('w-fit justify-self-center rounded-lg bg-nos-neutral-75 p-2', className)}>
      <QrCode skin="light" size={207} value={uri} />
    </div>
  );
}

function Divider() {
  return <hr className="h-px w-full border-0 bg-nos-neutral-200" />;
}

// ? the blue header is the brand "spoke": a full-bleed block whose bottom edge slopes up to the right
function SpokeCard({
  children,
  icon,
  title,
}: Readonly<{ children: ReactNode; icon: ReactNode; title: ReactNode }>) {
  return (
    <div className="grid overflow-hidden rounded bg-nos-neutral-75">
      <div className="relative grid h-[172px] place-items-center px-4">
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-nos-blue [clip-path:polygon(0_0,100%_0,100%_85%,0_100%)]"
        />
        <div className="relative mb-4 grid max-w-[311px] justify-items-center gap-2 text-center text-nos-neutral-0">
          {icon}
          <span className="font-bold font-ui-7">{title}</span>
        </div>
      </div>
      <div className="grid justify-items-center gap-8 px-4 py-6">{children}</div>
    </div>
  );
}

// ? the design system has no accordion, so this is the local disclosure used across the flows
function Disclosure({
  children,
  summary,
  tertiary,
  testId,
}: Readonly<{ children: ReactNode; summary: ReactNode; tertiary?: boolean; testId: string }>) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('grid', !tertiary && 'rounded bg-nos-neutral-75 p-4')}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        data-tid={testId}
        className={cn(
          'flex items-center text-left',
          tertiary
            ? 'h-10 w-fit gap-1 py-1 text-nos-blue underline font-ui-9'
            : 'justify-between gap-2 font-medium text-nos-neutral-1000 font-text-p2',
        )}
      >
        {summary}
        <ChevronDown
          width={tertiary ? 20 : 24}
          height={tertiary ? 20 : 24}
          className={cn('shrink-0', open && 'rotate-180')}
        />
      </button>
      {open && <div className={cn('grid gap-4', tertiary ? 'pt-2' : 'pt-4')}>{children}</div>}
    </div>
  );
}

function Footer({
  disabled,
  label,
  onClick,
}: Readonly<{ disabled?: boolean; label: ReactNode; onClick: () => void }>) {
  return (
    <footer className="grid pt-2">
      <Button
        skin="light"
        type="button"
        disabled={disabled}
        onClick={onClick}
        data-tid="validate-button"
        className="w-full px-4 tablet:w-fit tablet:justify-self-start"
      >
        {label}
      </Button>
    </footer>
  );
}
